import { useCallback, useEffect, useRef, useState } from "react";
import { presenceService } from "../../api/presenceService";
import { registerProfiles } from "./presence";

const POLL_MS = 60 * 1000;

/**
 * Polls `fn` every 60s while the tab is visible (paused on document.hidden,
 * refreshed when it becomes visible again). Returns { data, error, loading, reload }.
 */
export const usePolling = (fn, deps = [], { enabled = true, interval = POLL_MS } = {}) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const fnRef = useRef(fn);
  fnRef.current = fn;

  const queryId = useRef(0);
  const reload = useCallback(async () => {
    const id = queryId.current;
    try {
      const res = await fnRef.current();
      if (id !== queryId.current) return; // a newer query has started
      registerProfiles(res?.profiles);
      setData(res);
      setError("");
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Could not load presence");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    // New query (e.g. Today → Week): drop the previous result so it is never
    // shown as if it belonged to the new one.
    queryId.current += 1;
    setData(null);
    setLoading(true);
    reload();
    let timer = null;
    const start = () => {
      clearInterval(timer);
      timer = setInterval(() => !document.hidden && reload(), interval);
    };
    const onVisible = () => {
      if (!document.hidden) reload();
    };
    start();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [enabled, interval, reload, ...deps]); // eslint-disable-line react-hooks/exhaustive-deps

  return { data, error, loading, reload };
};

/**
 * Presence rows for one range, with the ranges already seen kept in memory so
 * stepping back and forth through history is instant (shown at once, then
 * refreshed in the background). Today's range still polls every 60s.
 */
export const usePresenceRange = ({ from, to }, { live = false, scope } = {}) => {
  const key = `${from}|${to}`;
  const cache = useRef(new Map());
  // The result is stored WITH the range it belongs to, so a render that already
  // has a new range never sees the previous range's rows (e.g. a whole week's
  // rows while in Day view, which duplicated people in keyed lists).
  const [state, setState] = useState({ key, data: null, error: "" });
  const view = state.key === key ? state : { key, data: cache.current.get(key) || null, error: "" };
  const queryId = useRef(0);

  useEffect(() => {
    const id = (queryId.current += 1);
    const load = async () => {
      try {
        const res = await presenceService.getPresence({ from, to, scope });
        if (id !== queryId.current) return; // a newer range was selected
        registerProfiles(res?.profiles);
        cache.current.set(key, res);
        setState({ key, data: res, error: "" });
      } catch (err) {
        if (id !== queryId.current) return;
        setState({
          key,
          data: cache.current.get(key) || null,
          error: err?.response?.data?.message || err?.message || "Could not load presence",
        });
      }
    };
    load();

    // Only the live range keeps refreshing.
    if (!live) return undefined;
    const timer = setInterval(() => !document.hidden && load(), POLL_MS);
    const onVisible = () => !document.hidden && load();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [key, from, to, live, scope]);

  return { data: view.data, error: view.error, loading: view.data === null && !view.error };
};

/** Live status (working / idle / offline) as a Map by userId, polled. */
export const useLivePresence = (enabled = true, scope) => {
  const res = usePolling(() => presenceService.getLiveStatus({ scope }), [scope], { enabled });
  const map = new Map((res.data?.people || []).map((p) => [p.userId, p]));
  return { ...res, live: map };
};
